import { compose } from 'redux';
import { connect } from 'react-redux';

import LinearProgress from '@mui/material/LinearProgress';

import Paper from '@mui/material/Paper';
import PropTypes from 'prop-types';
import React, { Component, Fragment } from 'react';
import { withTranslation } from 'react-i18next';
import {
    ListItemIcon, MenuItem, Table, TableBody, TableRow
} from '@mui/material';
import Button from '@mui/material/Button';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Menu from '@mui/material/Menu';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import TablePagination from '@mui/material/TablePagination';
import withPermCalc, { licenseManufacturerRouteName } from '../../HOCs/withPermCalc';

import { stableSort, getSorting, PersistentPaginationModel } from '../../../utils';
import CommonFab from '../../Shared/CommonFab';
import withTableHints from '../../HOCs/withTableHints';
import withTableStyles from '../../HOCs/withTableStyles';
import DeviceAssignmentReasonsAdd from '../DeviceAssignmentReasonsAdd/DeviceAssignmentReasonsAdd';
import {
    deleteAssignmentReason,
    fetchAssignmentReasons,
    saveAssignmentReason,
    updateAssignmentReason
} from '../../../actions/deviceAssignmentReasons';
import EnhancedTableHead, { SmallerTableCell } from '../../Shared/EnhancedTableHead';
import EmptyTableNotification from '../../Shared/EmptyTableNotification';
import TablePaginationActions from '../../Shared/TablePaginationActions';
import AlertDialog from '../../Shared/AlertDialog';
import ConfirmationDialog from '../../Shared/ConfirmationDialog';

const headerRows = [
    {
        id: 'name',
        numeric: false,
        disableSorting: false,
        disablePadding: false,
        label: 'deviceAssignmentReason',
    },
    {
        id: 'commentIsRequired',
        numeric: false,
        disableSorting: false,
        disablePadding: false,
        label: 'commentsAreRequired',
    },
    {
        id: 'description',
        numeric: false,
        disableSorting: false,
        disablePadding: false,
        label: 'description',
    }
];

const headerProtectedRows = [
    { id: 'actions', numeric: false, disableSorting: true, disablePadding: false, label: 'actions' }
];

const pageName = 'DEVICE_ASSIGNED_REASONS';

const persistentPaginationModel = new PersistentPaginationModel(pageName);

class DeviceAssignmentReasonsList extends Component {
    state = {
        order: 'asc',
        orderBy: 'name',
        page: 0,
        rowsPerPage: persistentPaginationModel.getItemsPerPage(),
        open: false,

        assignmentReasonId: '',
        assignmentReason: {},
        isCreateDialog: false,

        showAlert: false,
        alertDescription: '',
        alertVariant: 'info',

        showConfirmation: false,

        anchorEl: null
    };

    constructor(props) {
        super(props);

        const { editPerm } = this.props;

        if (editPerm) {
            this.headerRows = [...headerRows, ...headerProtectedRows];
        } else {
            this.headerRows = headerRows;
        }
    }

    componentDidMount() {
        this.props.fetchAssignmentReasons();
    }

    handleClickAdd = () => {
        this.setState({
            assignmentReasonId: '',
            assignmentReason: {},
            isCreateDialog: true,
            open: true
        });
    };

    handleClickDeleteConfirmation = () => {
        this.setState({ showConfirmation: true });
    };

    handleClickDelete = async () => {
        const { assignmentReasonId } = this.state;
        const { t } = this.props;
        const assignmentReason = this.props.assignmentReasons.find(type => type.id === assignmentReasonId);

        this.setState({ showConfirmation: false });

        if (!assignmentReason) {
            this.setState({
                alertDescription: t('errorCantFindAssignmentReason'),
                alertVariant: 'warning',
                showAlert: true
            });
        } else {
            try {
                await this.props.deleteAssignmentReason(assignmentReason);
                this.setState({
                    alertDescription: t('notificationAssignmentReasonWasDeleted'),
                    alertVariant: 'success',
                    showAlert: true
                });
            } catch (e) {
                if (this.props.error) {
                    this.setState({
                        alertDescription: this.props.error,
                        alertVariant: 'warning',
                        showAlert: true
                    });
                }
            }
        }
    };

    handleClickEdit = () => {
        const { assignmentReasonId } = this.state;
        const assignmentReason = this.props.assignmentReasons.find(type => type.id === assignmentReasonId);

        if (!assignmentReason) {
            this.setState({
                alertDescription: this.props.t('errorCantFindAssignmentReason'),
                alertVariant: 'warning',
                showAlert: true
            });
        } else {
            this.setState({
                assignmentReason,
                isCreateDialog: false,
                open: true
            });
        }
    };

    handleClose = () => {
        this.setState({ open: false });
    };

    handleAlertClose = (e, reason) => {
        if (reason === 'clickaway') return;

        this.setState({ showAlert: false });
    };

    handleConfirmationClose = () => {
        this.setState({ showConfirmation: false });
    };

    handleCreateAssignmentReason = async data => {
        try {
            await this.props.saveAssignmentReason(data);
            this.setState({
                open: false,
                alertDescription: this.props.t('notificationAssignmentReasonAdded'),
                alertVariant: 'success',
                showAlert: true
            });

            await this.props.fetchAssignmentReasons();
        } catch (e) {
            if (this.props.error) {
                this.setState({
                    alertDescription: this.props.error,
                    alertVariant: 'warning',
                    showAlert: true
                });
            }
        }
    };

    handleEditAssignmentReason = async data => {
        const { assignmentReasonId } = this.state;
        const { t } = this.props;
        const assignmentReason = this.props.assignmentReasons.find(type => type.id === assignmentReasonId);

        const params = {
            ...data,
            id: assignmentReasonId
        };

        if (!assignmentReason) {
            this.setState({
                alertDescription: t('errorCantFindDeviceAssignmentReason'),
                alertVariant: 'warning',
                showAlert: true
            });
        } else {
            try {
                await this.props.updateAssignmentReason(assignmentReasonId, params);
                this.setState({
                    open: false,
                    assignmentReason: {},
                    alertDescription: t('notificationAssignmentReasonUpdated'),
                    alertVariant: 'success',
                    showAlert: true
                });
            } catch (e) {
                if (this.props.error) {
                    this.setState({
                        alertDescription: this.props.error,
                        alertVariant: 'warning',
                        showAlert: true
                    });
                }
            }
        }
    };

    handleRequestSort = (event, property) => {
        const orderBy = property;
        let order = 'desc';

        if (this.state.orderBy === property && this.state.order === 'desc') {
            order = 'asc';
        }

        this.setState({ order, orderBy });
    };

    handleChangePage = (event, page) => {
        this.setState({ page });
    };

    handleChangeRowsPerPage = event => {
        const rowsPerPage = event.target.value;

        this.setState({
            rowsPerPage,
            page: 0
        });

        persistentPaginationModel.setItemsPerPage(rowsPerPage);
    };

    handleMenuClick = id => event => {
        this.setState({
            assignmentReasonId: id,
            anchorEl: event.currentTarget
        });
    };

    handleMenuClose = () => {
        this.setState({ anchorEl: null });
    };

    renderTable = () => {
        const { order, orderBy, rowsPerPage, page, anchorEl } = this.state;
        const { assignmentReasons, totalCount, classes, editPerm, t } = this.props;
        const configuredStableSort = compose(stableSort, getSorting)(order, orderBy);
        if (!totalCount) {
            return <EmptyTableNotification t={t} />;
        }

        return (
            <Fragment>
                <Paper>
                    <div className={classes.tableWrapper}>
                        <Table
                            className={classes.table}
                            stickyHeader
                        >
                            <EnhancedTableHead
                                headerRows={this.headerRows}
                                order={order}
                                orderBy={orderBy}
                                onRequestSort={this.handleRequestSort}
                                rowCount={assignmentReasons.length}
                                t={t}
                            />
                            <TableBody>
                                {configuredStableSort(assignmentReasons)
                                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                    .map(assignmentReason => (
                                        <TableRow
                                            className={classes.row}
                                            key={assignmentReason.id}
                                        >
                                            <SmallerTableCell>{assignmentReason.name}</SmallerTableCell>
                                            <SmallerTableCell>
                                                {`${assignmentReason.commentIsRequired ? t('yes') : t('no')}`}
                                            </SmallerTableCell>
                                            <SmallerTableCell>{assignmentReason.description}</SmallerTableCell>
                                            {editPerm && (
                                                <SmallerTableCell>
                                                    <Button
                                                        color="inherit"
                                                        aria-label="Menu"
                                                        aria-owns={anchorEl ? 'simple-menu' + assignmentReason.id : null}
                                                        aria-haspopup="true"
                                                        onClick={this.handleMenuClick(assignmentReason.id)}
                                                    >
                                                        <MoreVertIcon />
                                                    </Button>
                                                    <Menu
                                                        disableAutoFocusItem
                                                        id={'simple-menu' + assignmentReason.id}
                                                        anchorEl={anchorEl}
                                                        open={Boolean(
                                                            anchorEl && this.state.assignmentReasonId ===
                                                                assignmentReason.id
                                                        )}
                                                        onClose={this.handleMenuClose}
                                                    >
                                                        <MenuItem
                                                            key={'edit' + assignmentReason.id}
                                                            onClick={() => {
                                                                this.setState({ anchorEl: null },
                                                                    this.handleClickEdit);
                                                            }}
                                                        >
                                                            <ListItemIcon>
                                                                <EditIcon />
                                                            </ListItemIcon>
                                                            {t('edit')}
                                                        </MenuItem>
                                                        <MenuItem
                                                            key={'delete' + assignmentReason.id}
                                                            onClick={() => {
                                                                this.setState(
                                                                    { anchorEl: null },
                                                                    this.handleClickDeleteConfirmation
                                                                );
                                                            }}
                                                        >
                                                            <ListItemIcon>
                                                                <DeleteIcon />
                                                            </ListItemIcon>
                                                            {t('delete')}
                                                        </MenuItem>
                                                    </Menu>
                                                </SmallerTableCell>
                                            )}
                                        </TableRow>
                                    ))}
                            </TableBody>
                        </Table>
                    </div>
                </Paper>

                <TablePagination
                    component="div"
                    count={assignmentReasons.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    labelRowsPerPage={t('rowsPerPage')}
                    onPageChange={this.handleChangePage}
                    onRowsPerPageChange={this.handleChangeRowsPerPage}
                    ActionsComponent={TablePaginationActions}
                />
            </Fragment>
        );
    };

    render() {
        const { loading, addNewPerm, t } = this.props;

        return (
            <React.Fragment>
                <h2>
                    {t('deviceAssignmentReasons')}
                </h2>

                {this.state.open && (
                    <DeviceAssignmentReasonsAdd
                        open={this.state.open}
                        clickClose={this.handleClose}
                        clickSave={
                            this.state.isCreateDialog ?
                                this.handleCreateAssignmentReason : this.handleEditAssignmentReason
                        }
                        isLoading={loading}
                        title={
                            this.state.isCreateDialog ? t('addDeviceAssignmentReason') : t('editDeviceAssignmentReason')
                        }
                        data={this.state.assignmentReason}
                        t={t}
                    />
                )}

                <AlertDialog
                    open={this.state.showAlert}
                    variant={this.state.alertVariant}
                    onClose={this.handleAlertClose}
                    message={this.state.alertDescription}
                />

                <ConfirmationDialog
                    open={this.state.showConfirmation}
                    TitleButtomOk="yes"
                    TitleButtomCancel="no"
                    title={t('confirmation')}
                    description={t('deleteConfirmationMessage')}
                    handleOk={this.handleClickDelete}
                    handleClose={this.handleConfirmationClose}
                    t={t}
                />

                {loading ? <LinearProgress /> : this.renderTable()}

                {addNewPerm && (
                    <CommonFab
                        tooltipTranslateId="addDeviceAssignmentReason"
                        onClick={this.handleClickAdd}
                        t={t}
                    />
                )}
            </React.Fragment>
        );
    }
}

DeviceAssignmentReasonsList.propTypes = {
    classes: PropTypes.object.isRequired,
    fetchAssignmentReasons: PropTypes.func.isRequired,
    saveAssignmentReason: PropTypes.func.isRequired,
    deleteAssignmentReason: PropTypes.func.isRequired,
    updateAssignmentReason: PropTypes.func.isRequired,
    assignmentReasons: PropTypes.array,
    totalCount: PropTypes.number,
    error: PropTypes.string.isRequired,
    loading: PropTypes.bool,
    editPerm: PropTypes.bool,
    addNewPerm: PropTypes.bool,
};

const mapStateToProps = ({ deviceAssignmentReasons, user }) => ({
    assignmentReasons: deviceAssignmentReasons.data.items,
    totalCount: deviceAssignmentReasons.data.total,
    error: deviceAssignmentReasons.error,
    loading: deviceAssignmentReasons.loading,
    perms: user.perms
});

const mapDispatchToProps = {
    fetchAssignmentReasons,
    saveAssignmentReason,
    updateAssignmentReason,
    deleteAssignmentReason
};

export default compose(
    withTranslation('deviceAssignmentReasons'),
    connect(mapStateToProps, mapDispatchToProps),
    withTableStyles(),
    withPermCalc(licenseManufacturerRouteName),
    withTableHints
)(DeviceAssignmentReasonsList);
